<div class="mx-auto flex h-full w-full max-w-[1600px] flex-col gap-6 px-8 py-6 min-h-0">

  <!-- EB-3709: Read-only banner — surfaces only when the user lacks
       amie.brandManager.write. Gating + visuals live in the shared component;
       this page just drops it in unconditionally. -->
  <cmt-brand-readonly-banner />

  <!-- Hero card -->
  <section class="rounded-2xl border border-border bg-card p-6 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div class="flex min-w-0 flex-col gap-1">
        <p class="font-mono-feat text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Admin
        </p>
        <h1 class="font-display text-3xl font-semibold leading-tight text-foreground">
          Brands
        </h1>
        <p class="text-sm text-muted-foreground">
          Brands associated with your account.
        </p>
      </div>

      <div class="flex flex-none items-center gap-2 pt-1">
        <!-- Tooltip surfaces only in read-only mode; otherwise the button
             explains itself. Native [title] used here because hlmTooltip on a
             disabled <button> does not receive pointer events in all browsers. -->
        <button hlmBtn variant="default" (click)="newBrand()"
          [disabled]="!canWrite()"
          [attr.title]="!canWrite() ? 'Read-only access — needs amie.brandManager.write to enable' : null">
          <hlm-icon name="lucidePlus" size="0.9rem" /> New brand
        </button>
        <button hlmBtn variant="outline" (click)="newSubBrand()"
          [disabled]="!canWrite()"
          [attr.title]="!canWrite() ? 'Read-only access — needs amie.brandManager.write to enable' : null">
          <hlm-icon name="lucidePlus" size="0.9rem" /> New sub-brand
        </button>
      </div>
    </div>

    <!-- View controls: search + counter + show-inactive all in one row -->
    <div class="mt-5 flex flex-wrap items-center gap-4">
      <!-- Search -->
      <div class="relative max-w-md flex-1 min-w-[240px]">
        <hlm-icon
          name="lucideSearch"
          size="1rem"
          class="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          hlmInput
          size="sm"
          class="pl-11 pr-10"
          placeholder="Search by name, domain, or ID…"
          [value]="filterText()"
          (input)="filterText.set($any($event.target).value)"
        />
        @if (filterText()) {
          <button
            hlmBtn
            variant="ghost"
            size="icon"
            class="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
            type="button"
            tabindex="-1"
            (click)="filterText.set('')"
            aria-label="Clear search"
          >
            <hlm-icon name="lucideX" size="0.8rem" />
          </button>
        }
      </div>

      <!-- Counter chip — sits on a muted surface so it reads as a distinct data tag -->
      <div class="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1.5 font-mono-feat text-xs uppercase tracking-[0.1em] text-muted-foreground">
        @if (filterText()) {
          <span>{{ visibleBrandCount() }}</span>
          <span class="opacity-50">of</span>
          <span>{{ count() }} brands</span>
        } @else {
          <span class="text-foreground">{{ count() }}</span>
          <span>brands</span>
          <span class="opacity-40">·</span>
          <span class="inline-flex items-center gap-1.5">
            <span class="h-1.5 w-1.5 rounded-full bg-success"></span>
            {{ activeCount() }} active
          </span>
          <span class="opacity-40">·</span>
          <span class="inline-flex items-center gap-1.5">
            <span class="h-1.5 w-1.5 rounded-full bg-destructive"></span>
            {{ inactiveCount() }} inactive
          </span>
          <span class="opacity-40">·</span>
          <span>{{ subBrandCount() }} sub-brands</span>
        }
      </div>

      <!-- Subtle visual break -->
      <span class="h-5 w-px bg-border/80" aria-hidden="true"></span>

      <!-- Hide-inactive toggle. Default off — list shows the full catalogue
           so admin operators see ground truth (incl. inactive brands they
           may need to investigate); reduction is opt-in. -->
      <label class="inline-flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <input
          type="checkbox"
          class="h-4 w-4 cursor-pointer accent-primary"
          [checked]="hideInactive()"
          (change)="onHideInactiveChange($event)"
        />
        Hide inactive
      </label>
    </div>

    <!-- Discoverability hint — the row-click pattern (click anywhere in a row
         to open details + edit) isn't obvious without a label. -->
    <p class="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <hlm-icon name="lucideInfo" size="0.85rem" class="text-muted-foreground/70" />
      Click a brand row to open it for details &amp; editing; click an indented sub-brand to edit it.
    </p>
  </section>

  <!-- Grid / loading / empty -->
  @if (loading()) {
    <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
      <hlm-spinner size="20" /> Loading brands...
    </div>
  } @else if (brands().length === 0) {
    <div class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground">
      No brands found.
    </div>
  } @else if (rows().length === 0) {
    <div class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground">
      No brands match "<span class="font-mono-feat">{{ filterText() }}</span>".
    </div>
  } @else {
    <!-- ≥1024px: AG-Grid table. Definite-height wrapper + normal layout so the
         grid scrolls internally and pins the column header (native behaviour).
         The catalogue is small (~25 brands + sub-brands), all loaded eagerly
         client-side. Rows are the unified brand + indented sub-brand list. -->
    <div class="hidden h-[calc(100vh-15rem)] min-h-[18rem] flex-col lg:flex">
      <ag-grid-angular
        class="ag-theme-material cmt-brand-grid h-full"
        [rowData]="rows()"
        [columnDefs]="columnDefs"
        [defaultColDef]="defaultColDef"
        [pagination]="false"
        [suppressHorizontalScroll]="true"
        [getRowClass]="getRowClass"
        (rowClicked)="onRowClicked($event)"
      />
    </div>

    <!-- <1024px: stacked cards. Each brand card lists its sub-brands as compact
         indented sub-entries; orphan sub-brands appended at the end. -->
    <div class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1 lg:hidden">
      @for (brand of brandRows(); track brand.id) {
        <div class="rounded-xl border border-border bg-card shadow-sm" [class.cmt-brand-card-inactive]="brand.active === false">
          <div
            role="button"
            tabindex="0"
            class="group relative flex cursor-pointer items-start gap-5 rounded-xl p-4 transition hover:border-primary/40 hover:bg-accent/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            (click)="onBrandCardClick(brand)"
            (keydown.enter)="onBrandCardClick(brand)"
            (keydown.space)="onBrandCardClick(brand); $event.preventDefault()"
          >
            <!-- Logo — vertically centered against the full card height. -->
            @if (brand.logoDomain && getLogoUrl(brand.logoDomain); as logoUrl) {
              <img
                [src]="logoUrl"
                [alt]="brand.logoDomain"
                class="max-h-10 w-20 max-w-20 flex-none self-center object-contain"
                onerror="this.style.display='none'"
              />
            }

            <!-- Main content -->
            <div class="min-w-0 flex-1">
              <!-- Title row: dot + name + #id. Dot inline with the name anchors
                   it to the identity, not the logo. -->
              <div class="flex min-w-0 items-center gap-2">
                <span
                  class="h-2 w-2 flex-none rounded-full"
                  [class]="brand.active === false ? 'bg-destructive' : 'bg-success'"
                  [attr.aria-label]="brand.active === false ? 'Inactive' : 'Active'"
                  [title]="brand.active === false ? 'Inactive' : 'Active'"
                ></span>
                <h2 class="truncate text-base font-semibold leading-tight text-foreground">
                  {{ brand.name }}
                </h2>
                <span class="flex-none rounded-md border border-border bg-muted/50 px-1.5 py-0.5 font-mono-feat text-xs text-muted-foreground">
                  #{{ brand.id }}
                </span>
              </div>

              <p class="mt-0.5 truncate font-mono-feat text-xs text-muted-foreground">
                {{ brand.domain }}
              </p>

              <!-- Meta: inline values (country · city · currency). Tight,
                   info-dense, same column order as the grid's defaults. -->
              <p class="mt-2 flex flex-wrap items-center gap-x-2 text-xs text-foreground">
                <span class="truncate">{{ brand.defaultCountryName || '—' }}</span>
                <span class="text-muted-foreground/40" aria-hidden="true">·</span>
                <span class="truncate">{{ brand.defaultCityName || '—' }}</span>
                <span class="text-muted-foreground/40" aria-hidden="true">·</span>
                <span class="truncate font-mono-feat">{{ brand.currency || '—' }}</span>
              </p>
            </div>
          </div>

          <!-- Sub-brands nested under this brand. stopPropagation so the parent
               card's navigate doesn't fire when a sub-entry is clicked. -->
          @if (subBrandsFor(brand.id); as subs) {
            @if (subs.length > 0) {
              <ul class="border-t border-border/70 px-4 py-2">
                @for (sub of subs; track sub.id) {
                  <li
                    role="button"
                    tabindex="0"
                    class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition hover:bg-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    (click)="onSubBrandClick(sub); $event.stopPropagation()"
                    (keydown.enter)="onSubBrandClick(sub); $event.stopPropagation()"
                    (keydown.space)="onSubBrandClick(sub); $event.preventDefault(); $event.stopPropagation()"
                  >
                    <span class="text-muted-foreground/60" aria-hidden="true">&#8627;</span>
                    <span class="truncate text-foreground">{{ sub.name }}</span>
                    <span class="cmt-eyebrow flex-none rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-xs">
                      Sub-brand
                    </span>
                  </li>
                }
              </ul>
            }
          }
        </div>
      }

      <!-- Orphan sub-brands (no visible parent). -->
      @for (sub of orphanSubBrandRows(); track sub.id) {
        <div
          role="button"
          tabindex="0"
          class="flex cursor-pointer items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm shadow-sm transition hover:border-primary/40 hover:bg-accent/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          (click)="onSubBrandClick(sub)"
          (keydown.enter)="onSubBrandClick(sub)"
          (keydown.space)="onSubBrandClick(sub); $event.preventDefault()"
        >
          <span class="text-muted-foreground/60" aria-hidden="true">&#8627;</span>
          <span class="truncate text-foreground">{{ sub.name }}</span>
          <span class="cmt-eyebrow flex-none rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-xs">
            Sub-brand
          </span>
        </div>
      }
    </div>
  }
</div>
